import React, { useState, useEffect, useMemo } from "react";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { GoogleMapApiConfig } from "../config/GoogleMapApiConfig";

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  iconRtl: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
  inputRtl: {
    "& label": {
      right: 75,
      left: "auto",
    },
    "& legend": {
      textAlign: "right",
      marginRight: 60,
    },
  },
}));

export default function GoogleMapsAutoComplete(props) {
  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const countrylistdata = useSelector((state) => state.countrylistdata);
  const [restrictedCountries, setRestrictedCountries] = useState();

  useEffect(() => {
    if (countrylistdata.countries && countrylistdata.countries.length > 0) {
      const countries = countrylistdata.countries;
      let arr = [];
      for (let i = 0; i < countries.length; i++) {
        arr.push(countries[i].country_code.toLowerCase());
      }
      setRestrictedCountries(arr);
    }
  }, [countrylistdata.countries]);

  // Function for the Places API v1
  const fetchSuggestions = useMemo(
    () =>
      throttle(async (input, callback) => {
        if (!input) {
          callback([]);
          return;
        }

        try {
          // Create request body
          const requestBody = {
            input,
            // Add country restriction if available
            ...(restrictedCountries &&
              restrictedCountries.length > 0 && {
                includedRegionCodes: restrictedCountries.map((country) =>
                  country.toLowerCase()
                ),
              }),
          };

          // Make the API call
          const response = await fetch(
            "https://places.googleapis.com/v1/places:autocomplete",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": GoogleMapApiConfig,
                "X-Goog-FieldMask":
                  "suggestions.placePrediction.place,suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
              },
              body: JSON.stringify(requestBody),
            }
          );

          if (!response.ok) {
            throw new Error(`Places API error: ${response.status}`);
          }

          const data = await response.json();

          // Transform the response format to match what the component expects
          const suggestions = data.suggestions
            .filter((suggestion) => suggestion.placePrediction) // Only keep place predictions
            .map((suggestion) => {
              const placePrediction = suggestion.placePrediction;
              // Format the matched substrings to match the old API format
              const matches =
                placePrediction.text.matches?.map((match) => ({
                  offset: match.startOffset || 0,
                  length: (match.endOffset || 0) - (match.startOffset || 0),
                })) || [];

              // Extract secondary text from structured format
              const secondaryText =
                placePrediction.structuredFormat?.secondaryText || "";

              return {
                place_id: placePrediction.placeId,
                description: placePrediction.text.text,
                structured_formatting: {
                  main_text: placePrediction.text.text,
                  main_text_matched_substrings: matches,
                  secondary_text: secondaryText,
                },
              };
            });

          callback(suggestions);
        } catch (error) {
          console.error("Error fetching place predictions:", error);
          callback([]);
        }
      }, 200),
    [restrictedCountries]
  );

  // Function to get place details by place ID with the Places API v1
  const getPlaceDetails = async (placeId) => {
    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          method: "GET",
          headers: {
            "X-Goog-Api-Key": GoogleMapApiConfig,
            "X-Goog-FieldMask": "id,displayName,formattedAddress,location",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Places API error: ${response.status}`);
      }

      const placeDetails = await response.json();

      return {
        geometry: {
          location: {
            lat: () => placeDetails.location?.latitude,
            lng: () => placeDetails.location?.longitude,
          },
        },
        formatted_address: placeDetails.formattedAddress,
      };
    } catch (error) {
      console.error("Error fetching place details:", error);
      throw error;
    }
  };

  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetchSuggestions(inputValue, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetchSuggestions]);

  return (
    <Autocomplete
      style={props.style}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={props.value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue && newValue.place_id) {
          getPlaceDetails(newValue.place_id)
            .then((results) => {
              if (results) {
                newValue.coords = {
                  lat: results.geometry.location.lat(),
                  lng: results.geometry.location.lng(),
                };
                newValue.placeDetails = results;
              }
              props.onChange(newValue);
            })
            .catch((error) => {
              console.error("Error getting place details:", error);
              alert(t("google_places_error"));
            });
        } else {
          props.onChange(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.placeholder}
          variant={props.variant}
          className={
            isRTL === "rtl" ? classes.inputRtl : classes.commonInputStyle
          }
          fullWidth
        />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
          <li {...props} style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{
                  width: "calc(100% - 44px)",
                  wordWrap: "break-word",
                  textAlign: "start",
                }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}

                <Typography variant="body2" color="text.secondary">
                  {typeof option.structured_formatting.secondary_text ===
                  "string"
                    ? option.structured_formatting.secondary_text
                    : ""}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
